import { useQuery } from "@tanstack/react-query";
import type { StoredEvent } from "@facade/dashboard-shared";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ModelQueryDrawer } from "../components/ModelQueryDrawer";
import { api } from "../lib/api";
import { fmtCost, fmtDateTime, fmtMs, fmtNum, trunc } from "../lib/format";

export function ModelQueries() {
  const { deviceId } = useParams();
  const q = useQuery({ queryKey: ["model-queries", deviceId], queryFn: () => api.modelQueries({ deviceId, limit: 200 }), refetchInterval: 10_000 });
  const [open, setOpen] = useState<StoredEvent | null>(null);
  const rows = q.data ?? [];
  const totals = rows.reduce(
    (a, r) => ({ in: a.in + (r.tokens_in ?? 0), out: a.out + (r.tokens_out ?? 0), cost: a.cost + (r.cost_usd ?? 0), ms: a.ms + (r.latency_ms ?? 0), n: a.n + 1, err: a.err + (r.ok === 0 ? 1 : 0) }),
    { in: 0, out: 0, cost: 0, ms: 0, n: 0, err: 0 },
  );
  return (
    <div className="stack">
      <h1>Model queries {deviceId ? "" : "(all devices)"}</h1>
      <div className="panel">
        <div className="stats">
          <span className="stat"><span className="v">{fmtNum(totals.n)}</span><span className="l">queries (last 200)</span></span>
          <span className="stat"><span className="v">{fmtNum(totals.in)}</span><span className="l">tokens in</span></span>
          <span className="stat"><span className="v">{fmtNum(totals.out)}</span><span className="l">tokens out</span></span>
          <span className="stat"><span className="v">{totals.n ? fmtMs(totals.ms / totals.n) : "—"}</span><span className="l">avg latency</span></span>
          <span className="stat"><span className="v">{totals.err}</span><span className="l">errors</span></span>
          <span className="stat"><span className="v">{rows.some((r) => r.cost_usd != null) ? fmtCost(totals.cost) : "n/a"}</span><span className="l">cost</span></span>
        </div>
      </div>
      <div className="panel">
        {rows.length === 0 ? (
          <div className="empty">no model queries recorded</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>time</th>
                {!deviceId && <th>device</th>}
                <th>purpose</th>
                <th>model</th>
                <th>tokens</th>
                <th>latency</th>
                <th>run</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.event_id} className="clickable" onClick={() => api.modelQuery(r.event_id).then((d) => setOpen(d.event))}>
                  <td className="muted">{fmtDateTime(r.ts)}</td>
                  {!deviceId && <td>{r.nickname ?? r.device_id}</td>}
                  <td className="mono">{r.purpose}</td>
                  <td className="mono">{r.model}</td>
                  <td>
                    {fmtNum(r.tokens_in)} → {fmtNum(r.tokens_out)}
                  </td>
                  <td>{fmtMs(r.latency_ms)}</td>
                  <td>
                    {r.run_id ? (
                      <Link to={`/d/${r.device_id}/runs/${encodeURIComponent(r.run_id)}`} onClick={(e) => e.stopPropagation()}>
                        {trunc(r.run_prompt ?? r.run_id, 40)}
                      </Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{r.ok === 0 ? <span className="badge fail">error</span> : <span className="badge ok">ok</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {open && <ModelQueryDrawer e={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
